import { useState } from 'react';
import { MICROSOFT_LOGIN_URL, useRequestOtp, useVerifyOtp } from '@/queries/auth.queries';
import { Icon } from '@/components/ui/icons';
import { Button, Input, Spinner } from '@/components/ui/primitives';
import { useAppDispatch } from '@/store/hooks';
import { setCredentials } from '@/store/authSlice';
import { ApiError } from '@/lib/apiClient';

/** Microsoft's four-square brand mark. */
function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 21 21" className={className} aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const errorOf = (e: unknown) =>
  e instanceof ApiError ? e.message : 'Something went wrong. Please try again.';

// Email + one-time-code sign-in: enter email → receive a code → verify it.
function OtpForm() {
  const dispatch = useAppDispatch();
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const sendCode = async () => {
    setError(null);
    setNotice(null);
    try {
      await requestOtp.mutateAsync(email.trim());
      setStep('code');
      setNotice(`We sent a sign-in code to ${email.trim()}. It expires shortly.`);
    } catch (err) {
      setError(errorOf(err));
    }
  };

  const onSendSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendCode();
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const { user, token } = await verifyOtp.mutateAsync({ email: email.trim(), code: code.trim() });
      // Seed redux + localStorage; App re-renders into the authenticated app.
      dispatch(setCredentials({ user, token }));
    } catch (err) {
      setError(errorOf(err));
    }
  };

  const reset = () => {
    setStep('email');
    setCode('');
    setError(null);
    setNotice(null);
  };

  if (step === 'email') {
    return (
      <form onSubmit={onSendSubmit} className="space-y-3">
        <Input
          type="email"
          autoComplete="email"
          required
          placeholder="you@stfox.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Work email"
        />
        <Button type="submit" size="lg" className="w-full" disabled={requestOtp.isPending}>
          {requestOtp.isPending ? <Spinner className="h-4 w-4" /> : 'Email me a sign-in code'}
        </Button>
        {error && (
          <p role="alert" className="text-center text-sm text-destructive">
            {error}
          </p>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-3">
      <Input
        inputMode="numeric"
        autoComplete="one-time-code"
        required
        placeholder="6-digit code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        aria-label="Sign-in code"
        className="text-center tracking-[0.4em] text-lg"
      />
      <Button type="submit" size="lg" className="w-full" disabled={verifyOtp.isPending}>
        {verifyOtp.isPending ? <Spinner className="h-4 w-4" /> : 'Verify and sign in'}
      </Button>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <button type="button" onClick={reset} className="hover:text-foreground focus-ring rounded">
          ← Use a different email
        </button>
        <button
          type="button"
          onClick={() => void sendCode()}
          disabled={requestOtp.isPending}
          className="hover:text-foreground focus-ring rounded disabled:opacity-50"
        >
          Resend code
        </button>
      </div>
      {notice && !error && (
        <p className="text-center text-sm text-muted-foreground">{notice}</p>
      )}
      {error && (
        <p role="alert" className="text-center text-sm text-destructive">
          {error}
        </p>
      )}
    </form>
  );
}

export function Login({
  theme,
  onToggleTheme,
}: {
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const logoSrc =
    theme === 'dark' ? '/images/white-stfox-logo.png' : '/images/black-stfox-logo.png';

  // Surface an error handed back by the callback redirect (?error=…).
  const callbackError = new URLSearchParams(window.location.search).get('error');

  const signInWithMicrosoft = () => {
    setBusy(true);
    window.location.href = MICROSOFT_LOGIN_URL;
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <button
        onClick={onToggleTheme}
        className="absolute top-5 right-5 grid place-items-center w-10 h-10 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus-ring"
        title="Toggle theme"
      >
        {theme === 'dark' ? <Icon.sun /> : <Icon.moon />}
      </button>

      <div className="flex items-center justify-center min-h-screen px-4 py-10 sm:py-16">
        <div className="w-full max-w-sm p-4 animate-rise">
          <img
            src={logoSrc}
            alt="Saint Fox"
            className="mx-auto h-auto w-40 sm:w-56 max-w-full object-contain"
          />

          <div className="mt-8 space-y-3">
            <a
              href={MICROSOFT_LOGIN_URL}
              onClick={(e) => {
                e.preventDefault();
                signInWithMicrosoft();
              }}
              aria-disabled={busy}
              className="w-full inline-flex items-center justify-center gap-3 rounded-md border border-border bg-card hover:bg-accent transition-colors px-4 py-3 text-sm font-medium"
            >
              <MicrosoftLogo className="h-4 w-4" />
              {busy ? 'Redirecting…' : 'Continue with Microsoft'}
            </a>
          </div>

          {callbackError && (
            <p role="alert" className="mt-4 text-center text-sm text-destructive">
              {callbackError}
            </p>
          )}

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              Or with email
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="mt-6">
            <OtpForm />
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing you agree to the terms of internal use. For access issues, contact your IT
            administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
